import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from './Toast';
import s from './ToastViewport.module.css';

type ToastInput = { title: string; body?: string; error?: boolean };
type ToastItem = ToastInput & { id: string };
type ToastContextValue = (toast: ToastInput) => void;

const ToastContext = createContext<ToastContextValue | null>(null);

// Lightweight global toast host: useToast() pushes a transient message that auto-dismisses.
// Renders through the Toast primitive in a fixed viewport (portal). Wired in main.tsx.
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const counter = useRef(0);

  const push = useCallback((input: ToastInput) => {
    const id = `toast-${counter.current++}`;
    setItems((prev) => [...prev, { ...input, id }]);
    window.setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), 5000);
  }, []);

  return (
    <ToastContext.Provider value={push}>
      {children}
      {createPortal(
        <div className={s.viewport} aria-live="polite">
          {items.map((t) => (
            <Toast key={t.id} title={t.title} body={t.body} error={t.error} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook co-located with its provider
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}
